/* eslint-disable @typescript-eslint/camelcase */
import { batch } from 'react-redux';
import i18next from 'i18next';
import * as R from 'ramda';
import * as Sentry from '@sentry/browser';
import dayjs from 'dayjs';
import uuid from 'uuid/v1';

import * as saleApi from 'services/ErplyAPI/sales';
import { getHasUndismissedErrorsOfType } from 'reducers/Error';
import { modalPages as mp } from 'constants/modalPage';
import * as c from 'constants/sales';
import {
  getCurrentSalesDocument,
  getIsAReturn,
  getIsCurrentSaleAReturn,
  getIsSavedSale,
} from 'reducers/sales';
import {
  lastInvoiceNumbers,
  lastReceiptData,
  pendingRequests,
} from 'services/localDB';
import {
  getCurrencyCode,
  getReceiptTemplateIdByDocType,
  getSetting,
  getUseAgeVerification,
  getUseReceiptTemplates,
} from 'reducers/configs/settings';
import { getDefaultEmailInvoiceParams } from 'reducers/configs/emailInvoice';
import {
  getIsDefaultCustomer,
  getSelectedCustomer,
} from 'reducers/customerSearch';
import { getSelectedPos } from 'reducers/PointsOfSale';
import {
  ErplyAttributes,
  timestampInSeconds,
  getCausalStack,
  add,
  round,
} from 'utils';
import { notUndefinedOrNull } from 'utils/tsHelpers';
import {
  getHasRightToMakeASale,
  getLoggedInEmployeeID,
  getOrderModuleRights,
  getUserRightsAreFetched,
} from 'reducers/Login';
import { getManualPromotions, getPromotionByID } from 'reducers/CampaignsDB';
import {
  getAllPromotions,
  getAllPromotionsRewardPointCost,
  getCartHasSerializedGiftCardProduct,
  getHasBarItems,
  getHasKitchenItems,
  getHasProductsInShoppingCart,
  getIsProductNonDiscountable,
  getNumberOfProductsInCart,
  getProductsInShoppingCart,
  getPromoRulesProperties,
  getRowVatRateID,
  getShoppingCartForSalesDocument,
  getShoppingCartNeedsCalculation,
  getShoppingCartRows,
  getTotal,
  getTotalNet,
} from 'reducers/ShoppingCart';
import * as api from 'services/ErplyAPI/sales';
import { deselectCustomer, setCustomer } from 'actions/customerSearch';
import {
  addMultiProducts,
  applyPromotion,
  clearDiscountReasons,
  clearReturnReasons,
  resetShoppingCart,
  updateProductOrder,
} from 'actions/ShoppingCart';
import {
  addError,
  addSuccess,
  addWarning,
  dismiss,
  dismissAll,
  dismissType,
} from 'actions/Error';
import {
  openModalPage,
  openPaymentModal,
  checkIfBlockedExchange,
  waitForCartCalculationToFinish,
  closeModalPage,
  previousModalPage,
} from 'actions/modalPage';
import { createConfirmation } from 'actions/Confirmation';
import { doClientRequest } from 'services/ErplyAPI/core/ErplyAPI';
import * as jsonApi from 'services/JsonAPI';
import {
  printBarReceipt,
  printKitchenReceipt,
} from 'actions/integrations/printer';
import { getProductByID } from 'reducers/cachedItems/products';
import {
  getIgnoreCurrentOngoingSale,
  getIsUsingManualStoreCredit,
  getPayments,
  getRounding,
  getSalesDocument,
  getTotal as getPaymentsTotal,
} from 'reducers/Payments';
import { getSelectedPosVatRate, getVatRateByID } from 'reducers/vatRatesDB';
import {
  getUsedRewardPointRecords,
  getGiftCards,
  getProducts,
  getCustomers,
} from 'services/ErplyAPI';
import { sendSalesDocumentByEmail } from 'services/SalesAPI';
import { spreadCart } from 'utils/apiTransformations/saveSalesDocumentCart';
import { rowAttributeRegex } from 'utils/ErplyAttributes';
import { reentrantWebLocksRequest } from 'utils/concurrency';
import i18n from 'containers/App/i18n';

import { getPluginLifecycleHook } from '../reducers/Plugins';
import { SO } from '../services/DB/types';

import { resetProductReturn } from './returnProducts';
import { withProgressAlert } from './actionUtils';
import { getProductsUniversal, updateStockLevels } from './productsDB';
import { resetCache } from './cachedItems';
import {
  fetchUserRightsFromAPIsAndSetToState,
  setUserSalesData,
} from './Login';
import { resetPayments } from './Payments';
import {
  generateIssuedCouponRequests,
  printIssuedCoupons,
} from './CampaignsDB';
import { saveAttributesToSale } from './saleExtras';

export const getSalesDocs = () => ({
  type: c.GET_SALES_DOCUMENTS,
});

export const getSalesDocumentsSuccess = payload => ({
  type: c.GET_SALES_DOCUMENTS_SUCCESS,
  payload,
});

export const getSalesDocumentsFullResponseSuccess = payload => ({
  type: c.GET_SALES_DOCUMENTS_FULL_RESPONSE_SUCCESS,
  payload,
});

export const getSalesDocumentsError = payload => ({
  type: c.GET_SALES_DOCUMENTS_ERROR,
  payload,
});

export const getSalesDocuments = params => async dispatch => {
  dispatch(getSalesDocs());
  try {
    const payload = await api.getSalesDocuments(params);
    dispatch(getSalesDocumentsSuccess(payload));
  } catch (err) {
    console.error('Failed to load sales document', params, err);
    dispatch(getSalesDocumentsError(err));
  }
};

export const getSalesDocumentsCompleteResponse = params => async dispatch => {
  dispatch(getSalesDocs());
  try {
    const payload = await api.getSalesDocumentsCompleteResponse(params);
    dispatch(getSalesDocumentsFullResponseSuccess(payload));
  } catch (err) {
    if (err.toString() === 'TypeError: Failed to fetch') {
      dispatch(getSalesDocumentsFullResponseSuccess([]));
      return;
    }
    console.error('Failed to load sales document', params, err);
    dispatch(getSalesDocumentsError(err));
  }
};

export const setCurrentEmployee = employee => ({
  type: c.SET_SALE_EMPLOYEE,
  payload: employee,
});

export const setCurrentSalesDocument = param => ({
  type: c.SET_CURRENT_SALE_DOC,
  payload: param,
});

export const updateCurrentSaleDocument = payload => ({
  type: c.UPDATE_CURRENT_SALE_DOC,
  payload,
});

/**
 * @param {Object} params
 * @param {string} params.taxExemptCertificateNumber
 * @param {boolean} [params.isReseting]
 * @param {boolean} [params.isPartialExempt]
 */
export const updateCurrSaleDocTaxExNumber = ({
  taxExemptCertificateNumber,
  isReseting = false,
  isPartialExempt = false,
}) => async (dispatch, getState) => {
  // store vat rate before updating taxExemptCertificateNumber because after that the new vatrateID will override the existing one
  // and if the user removes the taxExemptCertificateNumber calculateShoppingCart will still return tax exempted prices

  const salesDoc = getCurrentSalesDocument(getState());
  if (
    isPartialExempt === salesDoc.isPartialExempt &&
    taxExemptCertificateNumber === (salesDoc.taxExemptCertificateNumber ?? '')
  ) {
    return;
  }

  const rows = getShoppingCartRows(getState());
  rows.forEach(({ user: { orderIndex, vatrateID, amount } }) =>
    dispatch(
      updateProductOrder({
        orderIndex,
        amount,
        vatrateID: isReseting ? undefined : vatrateID,
      }),
    ),
  );
  dispatch(
    updateCurrentSaleDocument({
      taxExemptCertificateNumber,
      isPartialExempt: !isReseting ? isPartialExempt : false,
    }),
  );
};

export const resetCurrentSalesDocument = () => async dispatch => {
  dispatch({ type: c.RESET_CURRENT_SALES_DOC });
  dispatch({ type: c.RESET_CURRENT_SALE_AS_RETURN });
  dispatch({ type: c.RESET_CURRENT_SALES_DOC_PAYMENTS });
};

export const setIsCurrentSaleAReturn = payload => ({
  type: c.SET_CURRENT_SALE_AS_RETURN,
  payload,
});

export const resetIsCurrentSaleAReturn = () => ({
  type: c.RESET_CURRENT_SALE_AS_RETURN,
});
export const resetCurrentSalesDocPayments = () => ({
  type: c.RESET_CURRENT_SALES_DOC_PAYMENTS,
});

export const setLastSalesDoc = payload => ({
  type: c.SET_LAST_SALES_DOC,
  payload,
});
export const resetLastSaleDoc = () => async dispatch => {
  dispatch({
    type: c.RESET_LAST_SALES_DOC,
  });
  lastReceiptData.reset();
};
export const setLastSaleActualReportsDataset = payload => ({
  type: c.SET_LAST_SALE_ACTUAL_REPORTS_DATASET,
  payload,
});

// new sales actions

export const getSales = params => async dispatch => {
  dispatch({ type: c.GET_SALES_DOCUMENTS });
  try {
    const {
      records,
      status: { recordsTotal, recordsInResponse },
    } = await api.getSalesDocumentsCompleteResponse(params);

    const byCustomerID = {};
    const all = {};
    records.forEach(r => {
      all[r.id] = r;
      byCustomerID[r.customerID] = byCustomerID[r.customerID]
        ? [...new Set([...byCustomerID[r.customerID], r.id])]
        : Array.of(r.id);
    });

    const payload = {
      recordsTotal,
      recordsInResponse,
      all,
      byCustomerID,
    };
    dispatch({ type: c.GET_SALES_DOCUMENTS_SUCCESS, payload });
  } catch (err) {
    console.error('Failed to load sales', params, err);
    dispatch({ type: c.GET_SALES_DOCUMENTS_ERROR, payload: err });
  }
};

export const getPrepaymentPercentages = () => async dispatch => {
  dispatch({ type: c.GET_PREPAYMENT_PERCENTAGES.START });
  try {
    const payload = await api.getPrepaymentPercentages();
    dispatch({ type: c.GET_PREPAYMENT_PERCENTAGES.SUCCESS, payload });
  } catch (err) {
    console.error('Failed to load percentage options for prepayment', err);
    dispatch(
      addError(i18next.t('alerts:loading.prePaymentPercentages_error'), {
        selfDismiss: true,
      }),
    );
    dispatch({ type: c.GET_PREPAYMENT_PERCENTAGES.SUCCESS, payload: err });
  }
};

export const setCurrentSalesDocPayments = ({
  documentID,
  paymentIDs = null,
}) => async (dispatch, getState) => {
  const { before, on, after } = getPluginLifecycleHook(
    'onSetCurrentSalesDocPayments',
  )(getState());
  try {
    await dispatch(before({ documentID, paymentIDs }));
    dispatch({ type: c.SET_CURRENT_SALES_DOC_PAYMENTS.START });
    const originalPayments = api.getPayments({ documentID });
    const returnPayments = paymentIDs
      ? api.getPayments({ paymentIDs: paymentIDs.join(',') })
      : Promise.resolve([]);

    const giftcardSerials = originalPayments
      .then(R.map(R.path(['attributes', 'giftCardID'])))
      .then(R.filter(id => id))
      .then(R.map(giftCardID => getGiftCards({ giftCardID })))
      .then(prs => Promise.all(prs))
      .then(R.unnest); // Responses come in as arrays, this unwraps the individual responses

    const originalWithBalance = Promise.all([
      originalPayments,
      giftcardSerials,
    ]).then(([pmts, gcards]) =>
      pmts.map(pmt => {
        if (pmt.type !== 'GIFTCARD') {
          return pmt;
        }
        if (!pmt.attributes?.giftCardID) {
          return pmt;
        }
        return R.mergeDeepLeft({
          giftCardBalance: gcards.find(
            gc => String(gc.giftCardID) === String(pmt.attributes.giftCardID),
          ).balance,
        })(pmt);
      }),
    );

    const { original, returned } = await dispatch(
      on(
        { documentID, paymentIDs },
        {
          original: await originalWithBalance,
          returned: await returnPayments,
        },
      ),
    );
    // Needs to await until payments are set in redux. Otherwise, if a return is opened quick enough (via automation), the payment list is shown empty
    await dispatch({
      type: c.SET_CURRENT_SALES_DOC_PAYMENTS.SUCCESS,
      payload: { original, returned },
    });
    dispatch(after({ documentID, paymentIDs }, null));
  } catch (err) {
    console.error(
      'Failed to load/set previous payment information for return',
      err,
    );
    dispatch({ type: c.SET_CURRENT_SALES_DOC_PAYMENTS.ERROR, payload: err });
  }
};

function revertPickup() {
  return async dispatch => {
    dispatch(addError(i18next.t('alerts:genericError')));
    dispatch(resetShoppingCart());
    dispatch(deselectCustomer());
    dispatch(resetCurrentSalesDocument());
  };
}
/**
 * Assign jdoc property to the row info before generating saveSalesDocument request.
 * @param {Object} props - properties, required for the action.
 * @param {Object} props.row - The row from shoppingCartForSaleDocument.
 * @param {Object} props.saleDocument - Sale document.
 * @param {Object[]} props.reduxCart - The cart, containing all the information, not just the valid fields for saveSalesDocument request.
 */
export function addJsonApiDataToRow({
  row,
  saleDocument,
  reduxCart,
  shouldStoreParentRowID,
}) {
  try {
    const creditedStableRowID =
      Number(row.stableRowID) && !!saleDocument.creditToDocumentID
        ? Number(row.stableRowID)
        : 0;

    const rowFromReduxCart = reduxCart.find(
      rcr => rcr.rowNumber === row.rowNumber,
    );
    const { notes, serialNumber, customerID, parentRowID } =
      rowFromReduxCart ?? {};

    // If nothing to save to JDOC, just return row as is
    if (
      !(
        notes ||
        creditedStableRowID ||
        serialNumber ||
        customerID ||
        parentRowID
      )
    ) {
      return row;
    }
    let parentRowCartIndex = -1;

    if (parentRowID) {
      parentRowCartIndex = reduxCart.findIndex(
        item => item.orderIndex === parentRowID,
      );
    }

    let existingJdoc;
    try {
      existingJdoc = JSON.parse(row.rowJDoc);
    } catch {
      existingJdoc = {};
    }

    /** @type jsonApi.JsonAPISuccessResponse.json_object */
    let newJdoc = {
      CreditInvoice: {
        creditedStableRowID,
      },
      BrazilPOS: {
        notes,
        serialNumber,
        customerID,
      },
    };

    if (
      shouldStoreParentRowID &&
      parentRowCartIndex !== -1 &&
      parentRowCartIndex !== row.orderIndex
    ) {
      newJdoc = R.assocPath(
        ['BrazilPOS', 'parentRowID'],
        // Increase by 1 since that's how ordering is made when saving rows info
        `$STABLE_ID_OF_ROW_${parentRowCartIndex + 1}$`,
      )(newJdoc);
    }

    /** @type jsonApi.JsonAPISuccessResponse.json_object */
    const mergedJdoc = R.reduce(
      R.mergeDeepWith((a, b) => b ?? a),
      jsonApi.emptyJdocForType.inv_rows,
      [existingJdoc, newJdoc],
    );
    return {
      ...row,
      rowJDoc: JSON.stringify(mergedJdoc),
    };
  } catch (e) {
    console.error(
      'Error calculating JSON data for row',
      { saleDocument, row },
      e,
    );
    return row;
  }
}

const printKitchenAndBarReceipts = lastSaleDocument => async (
  dispatch,
  getState,
) => {
  const state = getState();
  const shoppingCartHasBarItem = getHasBarItems(state);
  const shoppingCartHasKitchenItem = getHasKitchenItems(state);
  const mostRecentDoc = (
    await api.getSalesDocuments({ id: lastSaleDocument.invoiceID })
  )[0];
  const savedSalesDoc = getCurrentSalesDocument(state);
  const mostRecentDocRows = mostRecentDoc?.rows || [];
  const savedSalesDocRows = savedSalesDoc?.rows || [];

  await dispatch(
    getProductsUniversal(
      {
        productIDs: [...mostRecentDocRows, ...savedSalesDocRows].map(r =>
          Number(r.productID),
        ),
      },
      { addToCachedItems: true },
    ),
  );
  const hasAnAttribute = (item, attrName) =>
    Number(new ErplyAttributes(item?.attributes).get(attrName)) === 1;
  const kitchenProductsInSavedSales = savedSalesDocRows.filter(item => {
    return hasAnAttribute(
      getProductByID(item.productID)(state),
      'kitchen_item',
    );
  });
  const kitchenProductsInMostRecentDoc = mostRecentDocRows.filter(item => {
    return hasAnAttribute(
      getProductByID(item.productID)(state),
      'kitchen_item',
    );
  });
  const barProductsInSavedSales = savedSalesDocRows.filter(item => {
    return hasAnAttribute(getProductByID(item.productID)(state), 'bar_item');
  });
  const barProductsInMostRecentDoc = mostRecentDocRows.filter(item => {
    return hasAnAttribute(getProductByID(item.productID)(state), 'bar_item');
  });
  /*
    determine if anything has changed in the sale
    kitchen/bar should worry only about:
    amount, name, notes, addition/removal of product, nothing else matters
  */
  const kitchenItemsChanged = () => {
    const shortenedMostRecentKitchenItems = kitchenProductsInMostRecentDoc.map(
      item => {
        const container = {};
        container.stableRowID = item.stableRowID;
        container.name = item.itemName;
        container.amount = item.amount;
        container.notes = item?.jdoc?.BrazilPOS.notes;
        return container;
      },
    );
    const shortenedSavedSalesKitchenItems = kitchenProductsInSavedSales.map(
      item => {
        const container = {};
        container.stableRowID = item.stableRowID;
        container.name = item.itemName;
        container.amount = item.amount;
        container.notes = item?.jdoc?.BrazilPOS.notes;
        return container;
      },
    );
    return (
      JSON.stringify(shortenedMostRecentKitchenItems) !==
      JSON.stringify(shortenedSavedSalesKitchenItems)
    );
  };
  const barItemsChanged = () => {
    const shortenedMostRecentBarItems = barProductsInMostRecentDoc.map(item => {
      const container = {};
      container.stableRowID = item.stableRowID;
      container.name = item.itemName;
      container.amount = item.amount;
      container.notes = item?.jdoc?.BrazilPOS.notes;
      return container;
    });
    const shortenedSavedSalesBarItems = barProductsInSavedSales.map(item => {
      const container = {};
      container.stableRowID = item.stableRowID;
      container.name = item.itemName;
      container.amount = item.amount;
      container.notes = item?.jdoc?.BrazilPOS.notes;
      return container;
    });
    return (
      JSON.stringify(shortenedMostRecentBarItems) !==
      JSON.stringify(shortenedSavedSalesBarItems)
    );
  };
  if (shoppingCartHasKitchenItem) {
    if (kitchenItemsChanged()) {
      dispatch(printKitchenReceipt(mostRecentDoc));
    }
  }
  if (shoppingCartHasBarItem) {
    if (barItemsChanged()) {
      dispatch(printBarReceipt(mostRecentDoc));
    }
  }
};

export const checkRightToMakeSale = () => async (dispatch, getState) => {
  // If rights are not present in RDX
  if (!getUserRightsAreFetched(getState())) {
    // Re-fetch them
    try {
      await dispatch(fetchUserRightsFromAPIsAndSetToState());
    } catch (e) {
      dispatch(
        addError(
          i18next.t('alerts:errors.failedToFetchUserRights', {
            context: 'title',
          }),
        ),
      );
      throw new Error(
        i18next.t('alerts:errors.failedToFetchUserRights', {
          context: 'title',
        }),
      );
    }
  }
  // If user rights failed to re-fetch, throw error
  if (!getUserRightsAreFetched(getState())) {
    dispatch(
      addError(
        i18next.t('alerts:errors.failedToFetchUserRights', {
          context: 'title',
        }),
      ),
    );
    throw new Error(
      i18next.t('alerts:errors.failedToFetchUserRights', { context: 'title' }),
    );
  }
  // Rights were indeed fetched, but the right to make a sale is missing
  if (!getHasRightToMakeASale(getState())) {
    dispatch(addWarning(i18next.t('alerts:noSaleRights')));
    throw new Error(i18next.t('alerts:noSaleRights'));
  }
};

const promptToRetryOrReturnToPaymentModal = (props, err) => async (
  dispatch,
  getState,
) => {
  const isSavingPendingSale =
    !props.bonusRequests.length &&
    !props.payments.length &&
    !props.storeCreditPayments.length &&
    !Object.keys(props.salesDocument).length;

  const processedPaymentsCount = [
    ...props.storeCreditPayments,
    ...props.payments,
  ].length;

  const reason = err.cause?.messageForUI ?? err.cause?.cause?.messageForUI;

  await new Promise(resolve =>
    dispatch(
      createConfirmation(
        ({ submit: action }) => {
          if (action === 'retry') {
            dispatch(saveSalesDocument(props));
          } else if (action === 'cancelSale' && !isSavingPendingSale) {
            dispatch(
              openPaymentModal({
                props: { payments: getPayments(getState()) },
              }),
            );
          }
          resolve();
        },
        undefined,
        {
          title: i18next.t('alerts:payments.saving.title'),
          body: i18next.t('alerts:payments.saving.body', {
            reason,
            payments: processedPaymentsCount,
            context: reason ? 'withReason' : '',
          }),
          error: err,
          confirmText: [
            {
              name: i18next.t('alerts:payments.saving.cancelSale'),
              value: 'cancelSale',
            },
            {
              name: i18next.t('alerts:payments.saving.retry'),
              value: 'retry',
            },
          ],
          alertBeforeUnload: !!processedPaymentsCount,
        },
      ),
    ),
  );
};

const saveSalesDocumentBase = (
  /** @type {salesDocument?: any, payments?: any[], bonusRequests?: any[], onSuccess?: any} props */
  props = {
    salesDocument: {},
    payments: [],
    bonusRequests: [],
    storeCreditPayments: [],
  },
) => async (dispatch, getState, progress) => {
  try {
    await dispatch(checkRightToMakeSale());
    const state = getState();
    // temporaryUUID makes sure that repetitive requests would not cause duplicated documents
    const temporaryUUID = props?.temporaryUUID ?? uuid();
    // Plugins
    const {
      before: beforePlugin,
      on: onPlugin,
      after: afterPlugin,
    } = getPluginLifecycleHook('onSaveSalesDocument')(state);

    await dispatch(beforePlugin({ ...props, progress }));
    const ignoreCurrentSalesDocument = getIgnoreCurrentOngoingSale(state);

    const useAgeVerification = getUseAgeVerification(state);
    const onSuccess = props.onSuccess || (() => {});
    const currentSaleDoc = getCurrentSalesDocument(state);
    const { pointOfSaleID, warehouseID } = getSelectedPos(getState());
    const shouldStoreParentRowID = getSetting(
      'touchpos_group_products_in_shopping_cart',
    )(getState());

    const isPreviouslyCreatedInvoice =
      (props?.salesDocument?.type === 'INVOICE' ||
        props?.salesDocument?.type === 'INVWAYBILL') &&
      props?.salesDocument?.id;
    const salesDocument = isPreviouslyCreatedInvoice
      ? props.salesDocument
      : {
          ...currentSaleDoc,
          ...props.salesDocument,
          notes: props.salesDocument.notes || currentSaleDoc.notes,
        };

    const payments = props.payments || [];
    const bonusRequests = props.bonusRequests || [];
    dispatch({ type: c.SAVE_SALES_DOCUMENT.START });
    // if we are saving pending sale document, show warning that the doc is being saved
    const requests = [];
    const selectedCustomer = getSelectedCustomer(state);
    const { id: customerID } = selectedCustomer;
    const allowOffline = getSetting('touchpos_allow_offline_mode')(state);
    const loggedInEmployeeID = getLoggedInEmployeeID(state);
    const employeeID = currentSaleDoc?.employee?.id ?? loggedInEmployeeID;
    const updateDocumentDateWhenSavingPendingSales = getSetting(
      'update_document_date_when_saving_pending_sales',
    )(state);
    const updateDocumentCreatorWhenSavingPendingSales = getSetting(
      'update_document_creator_when_saving_pending_sales',
    )(state);
    const shoppingCart =
      salesDocument.modifiedRows ?? getProductsInShoppingCart(state);
    const currencyCode = getCurrencyCode(state);
    const allPromotions = getAllPromotions(state);
    // Why is this unused? Accidentally or intentionally removed feature and this is leftover?
    const { taxExemptCertificateNumber: taxExNr } = getCurrentSalesDocument(
      state,
    );
    const rulesProperties =
      salesDocument.type !== 'ORDER' ? getPromoRulesProperties(state) : {};
    const storeCred =
      Number(
        Object.values(getPayments(state)).find(
          pmt => pmt.type === 'STORECREDIT',
        )?.amount ?? 0,
      ) ?? 0;
    // Remove attributes from previous documents if making a return or a baseDocumentID exists (for orders, layawyas, etc).
    const attributes = new ErplyAttributes(
      salesDocument.type === 'CREDITINVOICE' || salesDocument.baseDocumentID
        ? undefined
        : salesDocument.attributes,
    );
    if (attributes.get('source-uuid')) {
      attributes.remove('source-uuid');
    }
    const salesDocumentTemplate = {
      temporaryUUID,
      currencyCode: salesDocument.currencyCode || currencyCode,
      customerID: props?.customerID || salesDocument.customerID || customerID,
      warehouseID,
      pointOfSaleID,
      confirmInvoice: Number(salesDocument.confirmed) || 0, // indicates if the saveDocument is to be completed or added to pending
      amountPaidWithStoreCredit: storeCred,
      type: salesDocument.type || 'CASHINVOICE',
      paymentType: salesDocument.paymentType,
      employeeID: salesDocument.employeeID || employeeID,
      attributes,
      notes: salesDocument.notes,
      internalNotes: salesDocument.internalNotes,
      baseDocumentID: salesDocument.baseDocumentID,
      taxExemptCertificateNumber: salesDocument.taxExemptCertificateNumber,
      requestName: 'saveSalesDocument',
      baseDocumentIDs: salesDocument.baseDocumentIDs || undefined,
      rounding: getRounding(state),
      invoiceState: salesDocument.invoiceState,
      workOrderID: salesDocument.workOrderID,
      ...rulesProperties,
    };

    if (props?.contactID) {
      Object.assign(salesDocumentTemplate, {
        contactID: props.contactID,
      });
    }

    if (selectedCustomer.paymentDays) {
      Object.assign(salesDocumentTemplate, {
        paymentDays: selectedCustomer.paymentDays,
      });
    }
    if (Number(salesDocument.confirmed) && getIsSavedSale(state)) {
      Object.assign(salesDocumentTemplate, {
        invoiceState: 'READY',
      });
    }

    if (storeCred === 0) {
      delete salesDocumentTemplate.amountPaidWithStoreCredit;
    }
    // "save sale" creates a CASHINVOICE sale that has the invoiceState set to "PENDING"
    const isPendingDocument =
      salesDocument.invoiceState === 'PENDING' && salesDocument.id;
    // handle Return Documents
    if (salesDocument.type === 'CREDITINVOICE') {
      if (isPendingDocument) {
        Object.assign(salesDocumentTemplate, {
          id: salesDocument.id,
          isCashInvoice: 1,
          creditInvoiceType: salesDocument.creditInvoiceType,
        });
      } else {
        Object.assign(salesDocumentTemplate, {
          creditToDocumentID: salesDocument.creditToDocumentID,
          // per PBIB-5077: KR's comment: “In fact, it would be necessary for Brazil POS to always include the parameters "isCashInvoice=1" when making a credit invoice. This would signal to Erply that this credit note should be calculated in the same way as cash receipts. (Somehow it is necessary to distinguish between a credit made from a cash receipt in the POS and a credit made from an invoice-waybill.)”
          isCashInvoice: 1,
          creditInvoiceType: salesDocument.creditInvoiceType,
        });
      }
    }

    if (useAgeVerification) {
      Object.assign(salesDocumentTemplate, {
        attributeName100: 'age-verification',
        attributeValue100: localStorage.getItem('age-verification'),
      });
    }
    if (salesDocument.invoiceState === 'PENDING') {
      if (updateDocumentDateWhenSavingPendingSales) {
        Object.assign(salesDocumentTemplate, {
          date: dayjs().format('YYYY-MM-DD'),
          time: dayjs().format('HH:mm:ss'),
        });
      }
      if (updateDocumentCreatorWhenSavingPendingSales) {
        Object.assign(salesDocumentTemplate, {
          employeeID: loggedInEmployeeID,
        });
      }
    }

    await (async function setInvoiceNumber() {
      // NB: When updating this code, also update the copy in modalpage.js
      // Search the code for reentrantWebLocksRequest('fetchNextInvoiceNumber' to find it

      const { confirmInvoice, type } = salesDocumentTemplate;
      // No invoice number for unconfirmed documents
      if (!confirmInvoice) {
        return;
      }
      // Default invoice numbers for other documents, API will handle it
      if (!['INVWAYBILL', 'CASHINVOICE', 'CREDITINVOICE'].includes(type)) {
        return;
      }
      const start = Date.now();
      salesDocumentTemplate.invoiceNo = await reentrantWebLocksRequest(
        'fetchNextInvoiceNumber',
        () =>
          lastInvoiceNumbers
            .getItem({ key: pointOfSaleID })
            .then(Number)
            .then(R.add(1))
            .then(R.when(Number.isNaN, R.always(null))),
      );
      if (salesDocumentTemplate.invoiceNo) {
        lastInvoiceNumbers.saveItem({
          key: pointOfSaleID,
          value: `${salesDocumentTemplate.invoiceNo}`,
        });
      }

      const end = Date.now();
      const elapsed = end - start;
      if (elapsed > 500) {
        Sentry.setContext('delay (ms)', { elapsed });
        Sentry.captureException(
          new Error('Unusually long delay waiting for the invoice number'),
        );
      }
    })();

    // Set original warehouse id if exists
    if (
      Number(salesDocument.confirmed) &&
      'INVWAYBILL' === salesDocumentTemplate.type
    ) {
      // warehouseID has to match original if sales doc is confirmed and causes inventory change
      Object.assign(salesDocumentTemplate, {
        warehouseID: salesDocument.warehouseID || warehouseID,
      });
    }
    // handle Layaways
    if (salesDocument.type === 'PREPAYMENT') {
      Object.assign(salesDocumentTemplate, {
        reserveGoods:
          salesDocument.invoiceState === 'CANCELLED'
            ? 0
            : salesDocument.reserveGoods,
        advancePayment: salesDocument.advancePayment,
        reserveGoodsUntilDate: salesDocument.reserveGoodsUntilDate,
        ...(typeof salesDocument.paymentDays === 'number' &&
        !Number.isNaN(salesDocument.paymentDays)
          ? { paymentDays: salesDocument.paymentDays }
          : {}),
      });
    }
    if (salesDocument.id && salesDocument.type !== 'CREDITINVOICE') {
      Object.assign(salesDocumentTemplate, {
        id: salesDocument.id,
      });
    }
    // Handle pending sales when saving them as order so they can reserve stock and have status confirmed in BO.
    const isPendingOrder = isPendingDocument && salesDocument.type === 'ORDER';
    if (isPendingOrder) {
      // Usually POS does not set reserveGoods on orders so API applies a correct value based on
      // the setting below. But in this case POS sets reserveGoods explicitly so it needs
      // to respect the setting.
      const orderShouldNotReserveGoods = getSetting(
        'order_does_not_reserve_goods',
      )(getState());
      const shouldReserveGoods = !Number(orderShouldNotReserveGoods);
      Object.assign(salesDocumentTemplate, {
        invoiceState: 'READY',
        reserveGoods: Number(shouldReserveGoods),
      });
    }

    const cart =
      salesDocument.modifiedRows ?? getShoppingCartForSalesDocument(getState());
    const cartWithJDoc = cart.map(row =>
      addJsonApiDataToRow({
        row,
        saleDocument: salesDocument,
        reduxCart: shoppingCart,
        shouldStoreParentRowID,
      }),
    );

    Object.assign(
      salesDocumentTemplate,
      spreadCart({
        cart: cartWithJDoc,
      }),
    );
    Object.assign(salesDocumentTemplate, getPromoRulesProperties(getState()));
    shoppingCart
      .filter(r => r.giftCardSerial)
      .forEach(r => {
        bonusRequests.push({
          value: r.finalPriceWithVAT,
          balance: r.finalPriceWithVAT,
          code: r.giftCardSerial,
          purchasingCustomerID: customerID,
          purchaseDateTime: timestampInSeconds(),
          purchaseWarehouseID: warehouseID,
          purchasePointOfSaleID: pointOfSaleID,
          purchaseEmployeeID: r.employeeID,
          purchaseInvoiceID: 'CURRENT_INVOICE_ID',
          added: timestampInSeconds(),
          vatrateID: r.vatrateID,
          requestName: 'saveGiftCard',
        });
      });

    const giftcardPeriodOfValidity = R.pipe(
      Number,
      R.when(Number.isNaN, () => 0),
    )(getSetting('giftcard_period_of_validity', '0')(getState()));
    if (giftcardPeriodOfValidity) {
      const now = new Date();
      now.setDate(now.getDate() + giftcardPeriodOfValidity);
      const expiration = now.toISOString().split('T')[0];
      bonusRequests
        .filter(req => req.requestName === 'saveGiftCard')
        .forEach(req => {
          // Only add/update period of validity when the gift card is bought, not redeemed
          if (req.purchaseDateTime) {
            req.expirationDate = expiration;
          }
        });
    }

    Object.assign(salesDocumentTemplate, {
      requestID: requests.length,
    });

    const getEmployeeStatsTemplate = {
      requestName: 'getEmployeeStats',
      warehouseID,
      pointOfSaleID,
    };

    const { type, id, invoiceState } = getSalesDocument(getState());
    const total = getPaymentsTotal(getState());

    const isReturn = Number(total) < 0;
    const isReturnToStoreCredit = storeCred < 0;
    const isOrderOrLayaway = ['ORDER', 'PREPAYMENT'].includes(type);
    const isExistingDocument = !!id;
    const isCancellation = invoiceState === 'CANCELLED';

    // Normally store credit payments are not saved at all, instead the other payments go directly into customer credit.
    // This does not happen for layaway cancellations because the layaway already has a total.
    // Thus, to cancel successfully, we need to save explicit payments
    const shouldSaveStoreCreditPayments =
      isReturn &&
      isReturnToStoreCredit &&
      isOrderOrLayaway &&
      isExistingDocument &&
      isCancellation;

    requests.push(salesDocumentTemplate);
    requests.push(getEmployeeStatsTemplate);
    if (Number(salesDocumentTemplate.confirmInvoice) === 1) {
      [...(props.storeCreditPayments ?? []), ...payments].forEach(p =>
        requests.push(
          R.pipe(
            R.assoc('requestName', 'savePayment'),
            R.assoc('requestID', requests.length),
            // remove terminalResponse from API payload since it is not supported and will be saved via JSON API
            R.dissoc('terminalResponse'),
            R.when(
              () => !!p.terminalResponse,
              R.assoc(
                'jdoc',
                JSON.stringify({ terminalResponse: p.terminalResponse }),
              ),
            ),
            R.when(p => p.typeID, R.dissoc('type')),
            R.when(
              p => p.addedToStoreCredit && shouldSaveStoreCreditPayments,
              R.assoc('type', 'CREDIT'),
            ),
          )(p),
        ),
      );
      if (shouldSaveStoreCreditPayments) {
        const storeCreditPaymentForBalance = {
          documentID: 'CURRENT_INVOICE_ID',
          customerID:
            props?.customerID || salesDocument.customerID || customerID,
          type: 'CREDIT',
          paymentServiceProvider: '',
          sum: storeCred,
          currencyCode: salesDocument.currencyCode || currencyCode,
          requestName: 'savePayment',
        };

        requests.push(storeCreditPaymentForBalance);
      }

      bonusRequests.forEach(b =>
        requests.push({
          ...b,
          requestID: requests.length,
        }),
      );
      const rewardPointCost = getAllPromotionsRewardPointCost(getState());
      if (rewardPointCost) {
        requests.push({
          requestName: 'subtractCustomerRewardPoints',
          points: rewardPointCost,
          invoiceID: 'CURRENT_INVOICE_ID',
          customerID: getSelectedCustomer(getState()).customerID,
          // TODO: campaignID
        });
      }
      if (
        salesDocument.creditInvoiceType === 'RETURN' &&
        salesDocument.usedRewardPoints
      ) {
        const returnTotal = shoppingCart
          .filter(row =>
            salesDocument.rows.some(r => r.stableRowID === row.stableRowID),
          )
          .map(row => Number(round(row.rowTotal, 2)))
          .reduce(add, 0);
        const pointsToAdd = Math.round(
          salesDocument.usedRewardPoints *
            (-returnTotal / round(salesDocument.total, 2)),
        );
        requests.push({
          requestName: 'addCustomerRewardPoints',
          points: pointsToAdd,
          invoiceID: 'CURRENT_INVOICE_ID',
          customerID,
        });
      }
      if (
        Object.keys(allPromotions).length &&
        salesDocumentTemplate.type !== 'ORDER'
      ) {
        requests.push({
          ...allPromotions,
          invoiceID: 'CURRENT_INVOICE_ID',
          requestName: 'addPromotionCountsToInvoice',
          requestID: requests.length,
        });
      }
    }
    // Update customer store credit if applicable
    // const storeCredit = salesDocumentTemplate.amountPaidWithStoreCredit;
    // if (storeCredit) {
    //   salesDocumentTemplate.amountPaidWithStoreCredit = Math.abs(storeCredit);
    // }
    // Any existing row-level attributes on the original document should be copied into the rows themselves and removed
    // This prevents attributes on the new document from referring to rows of the old
    // NOTE: This copying would be more logical to do when the document is loaded into cart, In that case checking for existing values is not necessary
    /** @type ErplyAttributes */
    new ErplyAttributes(salesDocument.attributes).asArray.forEach(
      ({ attributeName, attributeValue }) => {
        const match = attributeName.match(rowAttributeRegex);
        if (match) {
          const [, rowID, attrName] = match;
          // Remove it from the document
          salesDocumentTemplate.attributes.remove(attributeName);
          // If an order is not found, that could be because that specific row was not chosen to be returned
          const order = shoppingCart.find(
            order => String(order.stableRowID) === rowID,
          );
          if (order) {
            // Order might not have an .attributes yet, initialize it
            order.attributes = order.attributes ?? {};
            // Only override if it doesn't have a value yet
            // (if it has been assigned a new value we should ignore the value from the old sales document)
            order.attributes[attrName] =
              order.attributes[attrName] ?? attributeValue;
          }
        }
      },
    );
    Object.assign(
      salesDocumentTemplate,
      salesDocumentTemplate.attributes.asFlatArray,
    );
    delete salesDocumentTemplate.attributes;
    if (getIsUsingManualStoreCredit(state)) {
      delete salesDocumentTemplate.amountPaidWithStoreCredit;
    }

    /**
     * Variable that shows if a sale was created as a pending sale via "save sale"
     * Checks by invoiceNo being 0, type CASHINVOICE and if doc not confirmed
     */
    const isNewPendingSale =
      !salesDocumentTemplate.invoiceNo &&
      salesDocumentTemplate.type === 'CASHINVOICE' &&
      Number(salesDocumentTemplate.confirmInvoice) === 0;
    // In case of pending sale being created via "Save sale" do not print coupons
    if (!isNewPendingSale) {
      const issuedCouponRequests = await dispatch(
        generateIssuedCouponRequests({
          invoiceID: 'CURRENT_INVOICE_ID',
          salesDocument: salesDocumentTemplate,
        }),
      );
      requests.push(...issuedCouponRequests);
    }

    // Get data necessary for printing so that it is available as soon as printing starts
    requests.push({
      requestName: 'getSalesDocumentActualReportsDataset',
      salesDocumentID: 'CURRENT_INVOICE_ID',
    });

    try {
      const modifiedRequests = await dispatch(
        onPlugin({ ...props, progress }, requests),
      );
      const payload = await api.batchSaveSales(modifiedRequests);
      // At this point, if the action succeeded, both the sale document and payments are already saved to the BO
      const userSalesData =
        (
          payload.requests.find(
            request => request.status.requestName === 'getEmployeeStats',
          ) || {}
        ).records[0] || {};
      dispatch(setUserSalesData(userSalesData));

      const lastSaleDocumentIndex = payload.requests.findIndex(
        request => request.status.requestName === 'saveSalesDocument',
      );
      const lastSaleDocumentResponse = (payload.requests[lastSaleDocumentIndex]
        ?.records ?? [])[0];
      const lastSaleDocumentRequest = modifiedRequests[lastSaleDocumentIndex];
      let lastSaleDocument = lastSaleDocumentResponse
        ? { ...lastSaleDocumentResponse, type: lastSaleDocumentRequest?.type }
        : {};

      const lastSaleActualReportsDataset = payload.requests.find(
        request =>
          request.status.requestName === 'getSalesDocumentActualReportsDataset',
      )?.records?.[0];

      // Due to unknown reasons, sometimes the response will not contain rows
      // In that case we must refetch the document to get access to the rows
      // and specifically the stableRowIDs
      if (!lastSaleDocument.rows && lastSaleDocument.invoiceID) {
        console.warn(
          'saveSalesDocument response contained no rows! Attempting recovery',
          lastSaleDocument,
        );
        const [doc] = await saleApi.getSalesDocuments({
          id: lastSaleDocument.invoiceID,
        });
        if (doc && doc.rows) {
          console.log('Successfully recovered', doc);
          lastSaleDocument = {
            invoiceID: doc.id,
            invoiceNo: doc.number,
            customNumber: doc.customNumber,
            invoiceLink: doc.invoiceLink,
            receiptLink: doc.receiptLink,
            net: doc.netTotal,
            vat: doc.vatTotal,
            rounding: doc.rounding,
            total: doc.total,
            rows: doc.rows.map(row => ({
              rowID: row.rowID,
              stableRowID: row.stableRowID,
              productID: row.productID,
              serviceID: row.serviceID,
              amount: row.amount,
            })),
          };
        } else {
          console.error('Unable to recover document!', doc);
        }
      }

      dispatch(updateStockLevels(lastSaleDocument.rows));
      await dispatch(
        saveAttributesToSale({
          reduxCart: shoppingCart,
          lastSaleDocument,
          temporaryUUID,
        }),
      );

      const issuedCouponResponses = payload.requests.filter(
        request => request.status.requestName === 'saveIssuedCoupon',
      );
      if (issuedCouponResponses.length) {
        dispatch(printIssuedCoupons(issuedCouponResponses));
      }
      /**
       * Print kitchen/bar receipts in case setting enabled and certain products are sold.
       */
      if (getSetting('touchpos_print_kitchen_bar_receipts')(state)) {
        // Kitchen and Bar receipt printing seems to be error prone, thus wrapped in try catch
        try {
          await dispatch(printKitchenAndBarReceipts(lastSaleDocument));
        } catch {
          await dispatch(
            addError('Failed to print kitchen and/or bar receipt(s)'),
          );
          console.error('Failed to print kitchen and/or bar receipt(s)');
        }
      }

      batch(() => {
        dispatch(resetPayments());
        if (!ignoreCurrentSalesDocument) {
          dispatch({ type: c.SAVE_SALES_DOCUMENT.SUCCESS, payload });
          dispatch(resetProductReturn());
          dispatch(deselectCustomer());
          dispatch(resetCurrentSalesDocument());
          dispatch(resetCache(SO.PRODUCTS.NAME));
        }
        dispatch(setLastSalesDoc(lastSaleDocument));
        if (lastSaleActualReportsDataset) {
          dispatch(
            setLastSaleActualReportsDataset(lastSaleActualReportsDataset),
          );
        }
      });
      if (!ignoreCurrentSalesDocument) {
        await dispatch(resetShoppingCart());
      }
      lastReceiptData.set({
        salesDoc: { ...lastSaleDocument, employeeID, rows: shoppingCart },
      });
      dispatch(clearDiscountReasons());
      dispatch(clearReturnReasons());
      // Plugin afterSaveSaleDocument
      try {
        await dispatch(
          afterPlugin(
            { ...props, progress },
            {
              requests: modifiedRequests,
              responses: payload,
              salesDocument: lastSaleDocument,
            },
          ),
        );
      } catch (error) {
        /* 
          The outer catch block will try to save the sale document again 
          (if error occurs here the sale was already saved successfully) or void payments
          (impossible - payment state is already cleared) thus just notify about the error.
          It is plugin's responsibility to handle its error so if an error is caught here
          it mean that the plugin needs to be fixed. Re-dispatching the after hook
          is also not a good idea because it might duplicate non-idempotent operation calls 
        */
        await dispatch(
          addError(i18n.t('alerts:errors.onSaveSalesDocumentAfterFailed'), {
            selfDismiss: 10e3,
            dismissible: true,
          }),
        );
        console.error(error);
      }
      onSuccess({ type: salesDocument.type, ...lastSaleDocument });
    } catch (err) {
      if (err.errorCode === 1206) {
        return;
      }
      const offlineErrors = [
        'Failed to fetch',
        'Invalid or missing client code',
        'The internet connection appears to be offline',
        'Could not connect, please check your internet connection',
        'NetworkError when attempting to fetch resource',
        'Error: Forced offline mode is enabled',
      ];
      const shouldSaveOffline =
        allowOffline &&
        offlineErrors.some(e =>
          err
            .toString()
            .toLowerCase()
            .includes(e.toLowerCase()),
        );

      if (!shouldSaveOffline) {
        // Let outer catch block handle the error
        throw new Error(
          `Unhandled error when saving document (${err.message}), and not falling back to offline saving (was not a network error, or offline saving is not allowed)`,
          { cause: err },
        );
      }

      const offlineRequests = [];
      const offlineBonusRequests = props.bonusRequests || [];
      const getInvoiceNo = async () => {
        if (salesDocument.type !== 'ORDER') {
          const invoiceNo = await lastInvoiceNumbers.increment(pointOfSaleID);
          return invoiceNo;
        }
      };
      const invoiceNo = await getInvoiceNo();
      const offlineDocumentTemplate = {
        temporaryUUID,
        currencyCode: salesDocument.currencyCode || currencyCode,
        customerID: salesDocument.customerID || customerID,
        warehouseID: salesDocument.warehouseID || warehouseID,
        pointOfSaleID: salesDocument.pointOfSaleID || pointOfSaleID,
        confirmInvoice: salesDocument.confirmed || 0, // indicates if the saveDocument is to be completed or added to pending
        type: salesDocument.type || 'CASHINVOICE',
        invoiceNo: salesDocument.type === 'ORDER' ? undefined : invoiceNo,
        paymentType: salesDocument.paymentType,
        employeeID: salesDocument.employeeID || employeeID,
        ...new ErplyAttributes(salesDocument.attributes).asFlatArray,
        notes: salesDocument.notes,
        internalNotes: salesDocument.internalNotes,
        requestName: 'saveSalesDocument',
        added: timestampInSeconds(),
      };
      Object.assign(
        offlineDocumentTemplate,
        spreadCart({
          cart: (
            salesDocument.modifiedRows ??
            getShoppingCartForSalesDocument(getState())
          ).map(row => ({
            ...row,
            vatrateID:
              getRowVatRateID(row)(getState()) ??
              getSelectedPosVatRate(getState())?.id,
          })),
        }),
      );
      Object.assign(offlineDocumentTemplate, {
        requestID: offlineRequests.length,
      });
      offlineRequests.push(offlineDocumentTemplate);
      if (Number(offlineDocumentTemplate.confirmInvoice) === 1) {
        payments.forEach(p =>
          offlineRequests.push({
            ...p,
            requestName: 'savePayment',
            requestID: offlineRequests.length,
          }),
        );
        offlineBonusRequests.forEach(b =>
          offlineRequests.push({
            ...b,
            requestID: offlineRequests.length,
          }),
        );
      }

      if (
        Object.keys(allPromotions).length > 0 &&
        offlineDocumentTemplate.type !== 'ORDER'
      ) {
        offlineRequests.push({
          ...allPromotions,
          invoiceID: 'CURRENT_INVOICE_ID',
          requestName: 'addPromotionCountsToInvoice',
          requestID: offlineRequests.length,
        });
      }

      dispatch(updateStockLevels(shoppingCart));
      pendingRequests.add({
        request: 'batchSaveSales',
        params: offlineRequests,
      });
      batch(() => {
        dispatch(dismiss());
        dispatch(resetPayments());
        dispatch(resetShoppingCart());
        dispatch(deselectCustomer());
        dispatch(resetCurrentSalesDocument());
        dispatch(setLastSalesDoc(offlineDocumentTemplate));
        dispatch(resetCache(SO.PRODUCTS.NAME));
      });
      const documentResponseTemplate = {
        currencyCode: salesDocument.currencyCode || currencyCode,
        clientID: salesDocument.customerID || customerID,
        warehouseID: salesDocument.warehouseID || warehouseID,
        pointOfSaleID: salesDocument.pointOfSaleID || pointOfSaleID,
        confirmed: salesDocument.confirmed || 0, // indicates if the saveDocument is to be completed or added to pending
        type: salesDocument.type || 'CASHINVOICE',
        invoiceNo: salesDocument.type === 'ORDER' ? undefined : invoiceNo,
        invoiceID: salesDocument.type === 'ORDER' ? undefined : invoiceNo,
        number: salesDocument.type === 'ORDER' ? undefined : invoiceNo,
        paymentType: salesDocument.paymentType,
        employeeID: salesDocument.employeeID || employeeID,
        ...new ErplyAttributes(salesDocument.attributes).asFlatArray,
        notes: salesDocument.notes,
        internalNotes: salesDocument.internalNotes,
        netTotal: getTotalNet(getState()),
        total: getTotal(getState()),
        rows: shoppingCart,
      };
      lastReceiptData.set({ salesDoc: documentResponseTemplate });
      onSuccess({
        ...documentResponseTemplate,
      });
    }
  } catch (err) {
    batch(() => {
      dispatch({ type: c.SAVE_SALES_DOCUMENT.ERROR, payload: err });
      dispatch(dismiss());
    });
    // TODO: Polyfill error causal chain for chrome - maybe with `modern-errors` library?
    console.groupCollapsed('Error during saveSalesDocument', err);
    getCausalStack(err).forEach(err => console.error(err));
    console.groupEnd();
    Sentry.captureException(err);

    await dispatch(promptToRetryOrReturnToPaymentModal(props, err));
  }
};

export const saveSalesDocument = withProgressAlert(
  'alerts:payments.savingDocument',
  {
    errorType: 'savingDocument',
  },
)(saveSalesDocumentBase);

export const savePayments = ({
  documentID,
  payments,
  storeCreditPayments,
}) => async dispatch => {
  const requests = [...payments, ...storeCreditPayments].map(pmnt => ({
    ...pmnt,
    ...new ErplyAttributes(pmnt).asFlatArray,
    sum: pmnt.amount,
    documentID,
    request: 'savePayment',
  }));
  const results = await Promise.all(requests.map(req => doClientRequest(req)));
  dispatch(addSuccess(i18next.t('alerts:payments.success')));
  return results;
};

export const startNewSale = (params = { askForConfirmation: true }) => async (
  dispatch,
  getState,
) => {
  const { before, on, after } = getPluginLifecycleHook('onStartNewSale')(
    getState(),
  );
  const isPaymentModalOpening = getHasUndismissedErrorsOfType(
    'openPaymentModal',
  )(getState());

  if (isPaymentModalOpening) {
    return;
  }

  try {
    await dispatch(before(params));
    const state = getState();
    if (getNumberOfProductsInCart(state) > 0 && params.askForConfirmation) {
      await new Promise((resolve, reject) =>
        dispatch(
          createConfirmation(resolve, reject, {
            title: i18next.t('alerts:payments.void.confirm.title'),
            body: i18next.t('alerts:payments.void.confirm.body'),
          }),
        ),
      );
    }
    await dispatch(on(params, null));
    await dispatch(resetShoppingCart());
    batch(() => {
      dispatch(clearDiscountReasons());
      dispatch(clearReturnReasons());
      dispatch(deselectCustomer());
      dispatch(resetCurrentSalesDocument());
      dispatch(resetProductReturn());
    });
    await dispatch(after(params, null));
  } catch (e) {
    // User cancelled
  }
};

export const getPendingSales = () => async (dispatch, getState) => {
  const { pointOfSaleID: posID } = getSelectedPos(getState());
  const sales = await api.getSalesDocuments({
    type: 'CASHINVOICE',
    recordsOnPage: 100,
    confirmed: 0,
    posID,
  });
  return sales;
};

export const deletePendingSales = sales => () =>
  Promise.all(sales.map(s => api.deleteSalesDocument(s.id)));

// Previous and pending sales action to be dispatched form grid layout on Col2

const fetchingSalesData = (query, salesType) => async (dispatch, getState) => {
  const state = getState();
  dispatch(
    addWarning(i18next.t(`alerts:loading.${salesType}Sales`), {
      dismissible: false,
      selfDismiss: false,
      errorType: 'fetching_sales',
    }),
  );
  try {
    const { pointOfSaleID: posID } = getSelectedPos(state);
    const currentLocationOnly = getSetting(
      {
        recent: 'touchpos_recent_sales_only_from_current_location',
        pending: 'touchpos_unfinished_sales_only_from_current_location',
        order: 'touchpos_orders_only_from_current_location',
        layaway: 'touchpos_laybys_only_from_current_location',
        invoice: 'touchpos_invoices_only_from_current_location',
        offer: 'touchpos_offers_only_from_current_location',
      }[salesType],
    )(state);

    const queryParams = {
      recordsOnPage: 100,
      confirmed: 0,
      getRowsForAllInvoices: 1,
      getCustomerInformation: 1,
      posID,
      ...(currentLocationOnly
        ? {
            warehouseID: getSelectedPos(state).warehouseID,
            // pointOfSaleID: getSelectedPos(state).pointOfSaleID,
          }
        : {}),
      ...query,
    };

    const sales = await api.getSalesDocuments(queryParams);
    batch(() => {
      dispatch(dismissType('fetching_sales'));
      if (!sales.length) {
        dispatch(
          addWarning(
            i18next.t(`alerts:loading.${salesType}Sales`, { context: 'none' }),
            {
              selfDismiss: true,
            },
          ),
        );
      }
    });
    // TODO: maybe move this to the API request
    //  TODO: NB! (IF moving it there, do not forget to refactor actions/returnProducts.tsx fetchSalesDocs)
    // flattens the nested JSON API records in the productCard
    return sales.map(sale => ({
      ...sale,
      rows: sale.rows.map(row => ({
        ...row,
        ...row.jdoc?.BrazilPOS,
        orderIndex: row.stableRowID,
        // NB! Handles cases where sales Document has different currency than the current location
        // converts totals to default currency
        finalNetPrice: row.finalNetPrice * sale.currencyRate,
        finalPriceWithVAT: row.finalPriceWithVAT * sale.currencyRate,
        price: row.price * sale.currencyRate,
        rowNetTotal: row.rowNetTotal * sale.currencyRate,
        rowTotal: row.rowTotal * sale.currencyRate,
        rowVAT: row.rowVAT * sale.currencyRate,
      })),
      // NB! Handles cases where sales Document has different currency than the current location
      // converts totals to default currency
      netTotal: sale.netTotal * sale.currencyRate,
      netTotalsByTaxRate: sale.netTotalsByTaxRate.map(x => ({
        ...x,
        total: x.total * sale.currencyRate,
      })),
      total: sale.total * sale.currencyRate,
      vatTotal: sale.vatTotal * sale.currencyRate,
      vatTotalsByTaxRate: sale.vatTotalsByTaxRate.map(x => ({
        ...x,
        total: x.total * sale.currencyRate,
      })),
      paid: sale.paid ? String(sale.paid * sale.currencyRate) : sale.paid,
      currencyCode: getCurrencyCode(getState()),
      currencyRate: '0',
    }));
  } catch (err) {
    console.error('Failed to load sales of type', salesType, err);
    dispatch(
      addError(
        i18next.t(`alerts:loading.${salesType}Sales`, { context: 'error' }),
        {
          selfDismiss: true,
        },
      ),
    );
    return [];
  } finally {
    dispatch(dismissType('fetching_sales'));
  }
};

const openModalPageWithSales = (sales, salesType) => async dispatch => {
  const components = {
    pending: mp.pendingSales,
    recent: mp.recentSales,
    order: mp.pickupOrders,
    layaway: mp.layawayList,
    invoice: mp.unpaidInvoices,
  };
  dispatch(
    openModalPage({
      component: components[salesType],
      props: { sales },
    }),
  );
};

export const fetchRecentSales = (queryProps = {}) =>
  fetchingSalesData({ confirmed: 1, ...queryProps }, 'recent');

export const fetchRecentSalesAndOpenModal = () => async dispatch => {
  dispatch(openModalPageWithSales([], 'recent'));
};

export const fetchPendingSales = (queryProps = {}) =>
  fetchingSalesData({ type: 'CASHINVOICE', ...queryProps }, 'pending');

export const fetchUnpaidInvoices = (queryProps = {}) =>
  fetchingSalesData(
    {
      types: 'INVOICE,INVWAYBILL',
      confirmed: 1,
      unpaidItemsOnly: 1,
      getReturnedPayments: 1,
      ...queryProps,
    },
    'invoice',
  );

export const fetchUnpaidInvoicesAndOpenmodal = () => async dispatch => {
  const sales = await dispatch(fetchUnpaidInvoices());
  if (sales.length) {
    dispatch(openModalPageWithSales(sales, 'invoice'));
  }
};

export const fetchPendingSalesAndOpenModal = () => async dispatch => {
  const sales = await dispatch(fetchPendingSales());
  if (sales.length) {
    dispatch(openModalPageWithSales(sales, 'pending'));
  }
};

export const fetchOrderSales = (queryProps = {}) => (dispatch, getState) => {
  const orderRightsModule = getOrderModuleRights(getState());
  if (orderRightsModule?.view === 0) {
    dispatch(
      addError(i18next.t('alerts:noRightsToViewOrders'), {
        selfDismiss: 4e3,
        dismissible: true,
      }),
    );
    return [];
  }
  const employeeID = getLoggedInEmployeeID(getState());
  return dispatch(
    fetchingSalesData(
      {
        confirmed: 1,
        type: 'ORDER',
        getUnfulfilledDocuments: 1,
        invoiceState: 'NOT_CANCELLED',
        employeeID:
          employeeID && orderRightsModule?.view === 1 ? employeeID : undefined,
        ...queryProps,
      },
      'order',
    ),
  );
};

export const fetchOrderSalesAndOpenModal = () => async (dispatch, getState) => {
  const sales = await dispatch(fetchOrderSales());
  if (sales.length) {
    dispatch(openModalPageWithSales(sales, 'order'));
  }
};

export const fetchLayawaySales = queryProps =>
  fetchingSalesData(
    {
      confirmed: 1,
      type: 'PREPAYMENT',
      getUnfulfilledDocuments: 1,
      getReturnedPayments: 1,
      invoiceState: 'NOT_CANCELLED',
      ...queryProps,
    },
    'layaway',
  );

export const fetchLayawaySalesAndOpenModal = () => async dispatch => {
  const sales = await dispatch(fetchLayawaySales());
  if (sales.length) {
    dispatch(openModalPageWithSales(sales, 'layaway'));
  }
};

export const checkForLimitAmountPerSale = (
  productToEvaluate,
  shoppingCartItems,
  amount = null,
  orderIndex = null,
) => async dispatch => {
  const attrs = new ErplyAttributes(productToEvaluate.attributes);
  const limitPerSale = attrs.get('limit_amount_per_sale');
  const instancesInCart = shoppingCartItems.map(item =>
    item.name === productToEvaluate.name ? item : {},
  );
  const totalProductAmountInCart = instancesInCart.length
    ? instancesInCart.reduce((a, c) => {
        if (orderIndex === c.orderIndex && amount) {
          return a + Number(amount);
        }
        return a + Number(c.amount);
      }, 0)
    : null;

  const defineIfShouldAskForConfirmation = () => {
    if (amount) {
      if (
        Number(amount) <= Number(limitPerSale) &&
        totalProductAmountInCart <= Number(limitPerSale)
      ) {
        return false;
      }
    }

    if (totalProductAmountInCart + 1 > Number(limitPerSale)) {
      return true;
    }

    return false;
  };

  if (defineIfShouldAskForConfirmation()) {
    await i18next.loadNamespaces('limitPerSale');
    const amountLimitConfirmation = async dispatch =>
      new Promise((resolve, reject) => {
        dispatch(
          createConfirmation(resolve, reject, {
            title: i18next.t('limitPerSale:title', { limitPerSale }),
            body: i18next.t('limitPerSale:body'),
            confirmText: i18next.t('limitPerSale:confirmText'),
            cancelText: i18next.t('limitPerSale:cancelText'),
          }),
        );
      });

    const shouldAddProduct = await dispatch(amountLimitConfirmation).then(
      () => true,
      () => false,
    );

    return shouldAddProduct;
  }

  return true;
};

export const setUsedRewardPoints = returnDocument => async (
  dispatch,
  getState,
) => {
  const didUseRewardPoints = returnDocument.rows
    .reduce((all, row) => R.union(all, row.campaignIDs?.split(',')), [])
    .some(
      campaignID =>
        getPromotionByID(campaignID)(getState())?.formula ===
        'PAY_WITH_LOYALTY_POINTS',
    );
  if (didUseRewardPoints) {
    try {
      const date = new Date(returnDocument.date).getTime() / 1000;
      const daySeconds = 24 * 60 * 60;
      const rewardPointRecords = await getUsedRewardPointRecords({
        customerID: returnDocument.clientID,
        createdUnixTimeFrom: date - daySeconds,
        createdUnixTimeTo: date + daySeconds,
      });

      const relatedRecord = rewardPointRecords?.find(
        record => record.invoiceID === returnDocument.id,
      );
      if (!relatedRecord) {
        const error = new Error(
          'Invoice has PAY_WITH_LOYALTY_POINTS promotion on it, but no rewardPointRecord for the invoice could be found!',
          { returnDocument, rewardPointRecords },
        );
        Sentry.captureException(error);
        throw error;
      }

      dispatch({
        type: c.SET_USED_REWARD_POINTS,
        payload: relatedRecord.usedPoints,
      });
    } catch (error) {
      dispatch(
        addWarning('Unable to find amount of reward points to return', {
          selfDismiss: false,
        }),
      );
    }
  }
};

export const saveSale = () => async (dispatch, getState) => {
  const shoppingCartNeedsCalculation = getShoppingCartNeedsCalculation(
    getState(),
  );
  /**
   * Only referenced returns have the `isCurrentSaleAReturn` flag inside `sales` state set to true.
   */
  const isReferencedReturn = getIsCurrentSaleAReturn(getState());

  if (shoppingCartNeedsCalculation) {
    return dispatch(
      addWarning(i18next.t('gridButtons:alerts.calculateShoppingCart'), {
        selfDismiss: true,
        dismissible: true,
      }),
    );
  }
  const shoppingCartIsEmpty = !getHasProductsInShoppingCart(getState());
  if (shoppingCartIsEmpty) {
    return dispatch(
      addWarning(i18next.t('gridButtons:alerts.receiptEmptyError'), {
        selfDismiss: true,
        dismissible: false,
      }),
    );
  }

  if (isReferencedReturn) {
    return dispatch(
      addWarning(
        i18next.t('gridButtons:alerts.returnSaveError', { context: 'pending' }),
        {
          selfDismiss: true,
          dismissible: false,
        },
      ),
    );
  }

  const { id, type } = getCurrentSalesDocument(getState());
  if (!!id && type === 'ORDER') {
    return dispatch(
      addWarning(i18next.t('gridButtons:alerts.savingOrdersNotAllowed'), {
        selfDismiss: true,
        dismissible: false,
      }),
    );
  }

  const isBlockedExchange = await dispatch(checkIfBlockedExchange());
  if (isBlockedExchange) {
    return null;
  }

  try {
    await dispatch(waitForCartCalculationToFinish());
  } catch (error) {
    return null; // Timed out
  }

  const cartHasSerializedGiftCard = getCartHasSerializedGiftCardProduct(
    getState(),
  );
  if (cartHasSerializedGiftCard) {
    return dispatch(
      addWarning(
        i18next.t('gridButtons:alerts.giftCardOnPendingSaleNotAllowed'),
        {
          selfDismiss: true,
          dismissible: false,
        },
      ),
    );
  }
  dispatch(saveSalesDocument());
};

export const saveAsOrder = () => async (dispatch, getState) => {
  const orderRightsModule = getOrderModuleRights(getState());
  if (orderRightsModule?.add === 0) {
    return dispatch(
      addError(i18next.t('alerts:noRightsToAddOrders'), {
        selfDismiss: 4e3,
        dismissible: true,
      }),
    );
  }

  const shoppingCartNeedsCalculation = getShoppingCartNeedsCalculation(
    getState(),
  );
  if (shoppingCartNeedsCalculation) {
    return dispatch(
      addWarning(i18next.t('gridButtons:alerts.calculateShoppingCart'), {
        selfDismiss: true,
        dismissible: true,
      }),
    );
  }
  const shoppingCartIsEmpty = !getHasProductsInShoppingCart(getState());
  if (shoppingCartIsEmpty) {
    return dispatch(
      addWarning(i18next.t('gridButtons:alerts.receiptEmptyError'), {
        selfDismiss: true,
        dismissible: false,
      }),
    );
  }

  const isBlockedExchange = await dispatch(checkIfBlockedExchange());
  if (isBlockedExchange) {
    return null;
  }

  try {
    await dispatch(waitForCartCalculationToFinish());
  } catch (error) {
    return null; // Timed out
  }

  const isAReturn = getIsAReturn(getState());
  if (isAReturn) {
    return dispatch(
      addWarning(
        i18next.t('gridButtons:alerts.returnSaveError', {
          context: 'order',
        }),
        {
          selfDismiss: true,
          dismissible: false,
        },
      ),
    );
  }
  const currentSalesDocument = getCurrentSalesDocument(getState());
  const { id, type } = currentSalesDocument;
  if (!id || (id && ['PREPAYMENT', 'CASHINVOICE'].includes(type))) {
    dispatch(
      setCurrentSalesDocument({
        ...currentSalesDocument,
        type: 'ORDER',
        confirmed: 1,
      }),
    );
  }
  return dispatch(openPaymentModal({ showNotes: false }));
};

export const saveAsWaybill = () => async (dispatch, getState) => {
  const isDefaultCustomer = getIsDefaultCustomer(getState());
  if (isDefaultCustomer) {
    return dispatch(
      addWarning(
        i18next.t('gridButtons:alerts.defaultCustomerError', {
          context: 'waybill',
        }),
        {
          dismissible: false,
          selfDismiss: 3500,
        },
      ),
    );
  }
  const shoppingCartIsEmpty = !getHasProductsInShoppingCart(getState());
  if (shoppingCartIsEmpty) {
    return dispatch(
      addWarning(i18next.t('gridButtons:alerts.receiptEmptyError'), {
        selfDismiss: true,
        dismissible: false,
      }),
    );
  }

  const isBlockedExchange = await dispatch(checkIfBlockedExchange());
  if (isBlockedExchange) {
    return null;
  }

  try {
    await dispatch(waitForCartCalculationToFinish());
  } catch (error) {
    return null; // Timed out
  }

  const isAReturn = getIsAReturn(getState());
  if (isAReturn) {
    return dispatch(
      addWarning(
        i18next.t('gridButtons:alerts.returnSaveError', { context: 'waybill' }),
        {
          selfDismiss: true,
          dismissible: false,
        },
      ),
    );
  }
  const currentSalesDocument = getCurrentSalesDocument(getState());

  return dispatch(
    openPaymentModal({
      showNotes: false,
      props: {
        salesDocument: {
          ...currentSalesDocument,
          type: 'WAYBILL',
          confirmed: 1,
        },
      },
    }),
  );
};

export const saveAsLayaway = () => async (dispatch, getState) => {
  const shoppingCartIsEmpty = !getHasProductsInShoppingCart(getState());
  if (shoppingCartIsEmpty) {
    return dispatch(
      addWarning(i18next.t('gridButtons:alerts.receiptEmptyError'), {
        selfDismiss: true,
        dismissible: false,
      }),
    );
  }
  const isDefaultCustomer = getIsDefaultCustomer(getState());
  if (isDefaultCustomer) {
    return dispatch(
      addWarning(
        i18next.t('gridButtons:alerts.defaultCustomerError', {
          context: 'layaway',
        }),
        {
          dismissible: false,
          selfDismiss: 3500,
        },
      ),
    );
  }

  const isBlockedExchange = await dispatch(checkIfBlockedExchange());
  if (isBlockedExchange) {
    return null;
  }

  try {
    await dispatch(waitForCartCalculationToFinish());
  } catch (error) {
    return null; // Timed out
  }

  const shoppingCartNeedsCalculation = getShoppingCartNeedsCalculation(
    getState(),
  );
  if (shoppingCartNeedsCalculation) {
    return dispatch(
      addWarning(i18next.t('gridButtons:alerts.calculateShoppingCart'), {
        selfDismiss: true,
        dismissible: true,
      }),
    );
  }
  const isAReturn = getIsAReturn(getState());
  if (isAReturn) {
    return dispatch(
      addWarning(
        i18next.t('gridButtons:alerts.returnSaveError', { context: 'layaway' }),
        {
          selfDismiss: true,
          dismissible: false,
        },
      ),
    );
  }
  const currentSalesDocument = getCurrentSalesDocument(getState());
  const { id, type } = currentSalesDocument;
  if (!id || (id && type === 'ORDER')) {
    dispatch(
      setCurrentSalesDocument({
        ...currentSalesDocument,
        type: 'PREPAYMENT',
        confirmed: 1,
      }),
    );
  }
  return dispatch(openModalPage({ component: mp.layaway }));
};

export const saveAsAccountSale = () => async (dispatch, getState) => {
  const shoppingCartIsEmpty = !getHasProductsInShoppingCart(getState());
  const isDefaultCustomer = getIsDefaultCustomer(getState());
  const currentSalesDocument = getCurrentSalesDocument(getState());
  const customer = getSelectedCustomer(getState());
  const customerIsCashOnly = Boolean(customer.salesBlocked);

  const { id, type, total: saleTotal, paid } = currentSalesDocument;

  if (shoppingCartIsEmpty) {
    return dispatch(
      addWarning(i18next.t('gridButtons:alerts.receiptEmptyError'), {
        selfDismiss: true,
        dismissible: false,
      }),
    );
  }
  if (isDefaultCustomer) {
    return dispatch(
      addWarning(
        i18next.t('gridButtons:alerts.defaultCustomerError', {
          context: 'accountSales',
        }),
        {
          dismissible: false,
          selfDismiss: 3500,
        },
      ),
    );
  }
  if (customerIsCashOnly) {
    return dispatch(
      addWarning(i18next.t('gridButtons:alerts.customerIsCashOnly'), {
        selfDismiss: true,
        dismissible: false,
      }),
    );
  }

  const isBlockedExchange = await dispatch(checkIfBlockedExchange());
  if (isBlockedExchange) {
    return null;
  }

  try {
    await dispatch(waitForCartCalculationToFinish());
  } catch (error) {
    return null; // Timed out
  }

  const total = getTotal(getState());
  const outstandingAmount = saleTotal - paid || total;
  if (outstandingAmount > customer.availableCredit) {
    return dispatch(
      addWarning(i18next.t('gridButtons:alerts.creditLimitOver'), {
        dismissible: false,
        selfDismiss: 3500,
      }),
    );
  }
  if (!id || (id && type === 'ORDER')) {
    const {
      notes,
      internalNotes,
      taxExemptCertificateNumber,
      isPartialExempt,
    } = currentSalesDocument;
    dispatch(
      setCurrentSalesDocument({
        notes,
        internalNotes,
        taxExemptCertificateNumber,
        isPartialExempt,
        type: 'INVWAYBILL',
        confirmed: 1,
        ...(id ? { baseDocumentID: id } : {}),
      }),
    );
  }
  return dispatch(openModalPage({ component: mp.accountSales }));
};

/**
 * @param { import("../types/SalesDocument").SaleDocumentResponse} sale
 */
const pickupOrderBase = sale => async (dispatch, getState) => {
  const { before, on, after } = getPluginLifecycleHook('onPickupOrder')(
    getState(),
  );
  try {
    await dispatch(before(sale));

    const modifiedSale = await dispatch(on(sale, sale));

    await dispatch(resetShoppingCart());

    await dispatch(setCustomer({ data: modifiedSale.clientID }));
    dispatch(setCurrentSalesDocument(modifiedSale));

    dispatch(
      setCurrentSalesDocPayments({
        documentID: modifiedSale.id,
      }),
    );
    // NB! Make sure that cart is recalculated AFTER sale document and customer are set.
    // Certain document and customer fields might impact calculation and omitting them will result in
    // incorrectly calculated cart, for example, twice applied promotions.
    dispatch(addMultiProducts(modifiedSale.rows));

    await dispatch(after(sale, modifiedSale));
  } catch (error) {
    // In case there was some sort of error during order pickup process (f.e. customer, cur doc, cur doc payments were set, but the cart failed to get set, need to reset everything to prevent partial redux state)
    console.warn('Order pickup failed. Error: ', error);
    dispatch(revertPickup());
  }
};

export const pickupOrder = withProgressAlert('order:alerts.pickingUpOrder')(
  pickupOrderBase,
);

/**
 * @param { import("../types/SalesDocument").SaleDocumentResponse} sale
 */
const pickupPendingSaleBase = sale => async (dispatch, getState) => {
  const { before, on, after } = getPluginLifecycleHook('onPickupPendingSale')(
    getState(),
  );
  try {
    await dispatch(before(sale));

    const shouldGetPromotions = sale.rows.some(
      ({ discount, stableRowID }) => Number(discount) > 0 && stableRowID,
    );
    const promos = !shouldGetPromotions
      ? []
      : await doClientRequest({
          request: 'getAppliedPromotionRecords',
          invoiceIDs: sale.id,
        }).catch(err => {
          console.error(
            'Failed to fetch applied promotions for sale',
            sale,
            err,
          );
          return [];
        });

    const products = await getProducts({
      productIDs: sale.rows.map(row => row.productID).join(','),
    })
      .then(({ records }) => records)
      .catch(err => {
        console.error('Failed to pick up pending sale', sale, err);
        return [];
      });

    const getProductsToAdd = () =>
      sale.rows.map(product => {
        const isNonDiscountable = getIsProductNonDiscountable(product)(
          getState(),
        );
        const getDiscountFromPromo = () => {
          // promo associated with row
          const promo = (promos ?? []).find(
            promo =>
              Number(promo.stableRowID) === Number(product.stableRowID) &&
              Number(promo.manualDiscountPercentage),
          );

          if (!promo) {
            return undefined;
          }

          // if manual discount on product, use it instead of promo manual discount
          const { manualDiscountPercentage } = promo;

          return manualDiscountPercentage;
        };
        const newProduct = { ...product };
        if (!newProduct.originalPriceIsZero) {
          delete newProduct.price;
          delete newProduct.finalNetPrice;
          delete newProduct.finalPriceWithVAT;
        }
        if (!isNonDiscountable) {
          const manualDiscountToApply = getDiscountFromPromo();
          if (manualDiscountToApply) {
            newProduct.manualDiscount = manualDiscountToApply;
          }
          delete newProduct.discount;
        }

        // Check if VAT rate is implicitly set and if it is remove VAT rate ID
        const productObj = products.find(
          p => String(p.productID) === String(newProduct.productID),
        );
        const productVatRate = getVatRateByID(productObj?.vatrateID)(
          getState(),
        );
        const selectedPosVatRate = getSelectedPosVatRate(getState());
        const selectedCustomer = getSelectedCustomer(getState());

        const defaultVatRate = selectedPosVatRate || productVatRate;
        const isDefaultVatRate = newProduct.vatrateID === defaultVatRate?.id;
        const isImplicitVatRate =
          isDefaultVatRate || selectedCustomer.taxExempt;
        if (isImplicitVatRate) {
          delete newProduct.vatrateID;
        }
        return newProduct;
      });

    const threw = Symbol('threw');
    const modifiedSale = await dispatch(on(sale, sale)).catch(() => threw);
    if (modifiedSale === threw) {
      return;
    }

    dispatch(resetShoppingCart());

    dispatch(setCurrentSalesDocument(modifiedSale));
    await dispatch(setCustomer({ data: modifiedSale.clientID }));
    // NB! Make sure that cart is recalculated AFTER sale document and customer are set.
    // Certain document and customer fields might impact calculation and omitting them will result in
    // incorrectly calculated cart, for example, twice applied promotions.
    await dispatch(addMultiProducts(getProductsToAdd()));

    // Apply manual promotions if they are active and were applied when sale was saved
    const activeManualPromotions = getManualPromotions(getState());

    promos.forEach(promo => {
      const activeManualPromotion = activeManualPromotions.find(
        mp => Number(mp.campaignID) === Number(promo.promotionID),
      );
      if (activeManualPromotion) {
        dispatch(applyPromotion({ ...activeManualPromotion, amount: 1 }));
      }
    });

    await dispatch(after(sale, modifiedSale));
  } catch (e) {
    console.warn('Pending sale pickup failed. Error: ', e);
    dispatch(revertPickup());
  }
};

export const pickupPendingSale = withProgressAlert(
  'sale:pendingSales.alerts.loadingPickup',
  {
    errorType: 'saved-sale-pickup',
  },
)(pickupPendingSaleBase);

/**
 * @param { import("../types/SalesDocument").SaleDocumentResponse} sale
 */
const cancelLayawayBase = sale => async dispatch => {
  try {
    const customerPaid =
      Number(sale.paid) +
      (sale.returnedPayments?.map(p => Number(p.sum)).reduce(add, 0) ?? 0);

    const customer = getCustomers({
      customerID: sale.clientID,
      getBalanceInfo: 1,
      getBalanceWithoutPrepayments: 1,
    }).then(([customer]) => customer);

    const originalPayments = api.getPayments({ documentID: sale.id });
    const returnPayments = sale.returnedPayments.length
      ? api.getPayments({
          paymentIDs: sale.returnedPayments.flatMap(p => p.ids).join(','),
        })
      : Promise.resolve([]);

    dispatch(closeModalPage());

    dispatch(
      openPaymentModal({
        props: {
          isReturn: true,
          ignoreCurrent: true,
          salesDocument: {
            clientID: sale.clientID,
            rows: R.map(R.evolve({ amount: R.negate }))(sale.rows),
            total: -sale.total,
            type: 'PREPAYMENT',
            invoiceState: 'CANCELLED',
            advancePayment: sale.advancePayment,
            id: sale.id,
          },
          currentSalesDocument: sale,
          originalPayments: await originalPayments,
          returnPayments: await returnPayments,
          customer: await customer,
          total: -customerPaid,
        },
      }),
    );
  } catch (error) {
    dispatch(addError(i18next.t('alerts:genericError')));
  }
};

export const cancelLayaway = withProgressAlert('layaway:alerts.cancelLayaway')(
  cancelLayawayBase,
);

/**
 * @param { import("../types/SalesDocument").SaleDocumentResponse} sale
 */
const payFullyForLayawayBase = sale => async (dispatch, getState) => {
  const { before, on, after } = getPluginLifecycleHook('onPickupLayaway')(
    getState(),
  );

  try {
    await dispatch(before(sale));

    const threw = Symbol('threw');

    const modifiedSale = await dispatch(on(sale, sale)).catch(() => threw);
    if (modifiedSale === threw) {
      return;
    }

    await dispatch(resetShoppingCart());
    // TODO: Also pass products and customer explicitly
    //  products: .rows2
    //  customer: .clientID
    await dispatch(setCustomer({ data: modifiedSale.clientID }));
    await dispatch(setCurrentSalesDocument(modifiedSale));
    await dispatch(
      setCurrentSalesDocPayments({
        documentID: modifiedSale.id,
      }),
    );
    // NB! Make sure that cart is recalculated AFTER sale document and customer are set.
    // Certain document and customer fields might impact calculation and omitting them will result in
    // incorrectly calculated cart, for example, twice applied promotions.
    await dispatch(addMultiProducts(modifiedSale.rows));

    dispatch(closeModalPage());

    dispatch(
      openPaymentModal({
        props: {
          salesDocument: modifiedSale,
          resetSalesDocumentOnClose: false,
        },
      }),
    );

    await dispatch(after(sale, modifiedSale));
  } catch (error) {
    // In case there was some sort of error between layaway pickup process (f.e. customer, cur doc, cur doc payments were set, but the cart failed to get set, need to reset everything to prevent partial redux state)
    dispatch(revertPickup());
  }
};

export const payFullyForLayaway = withProgressAlert(
  'layaway:alerts.loadingFullLayaway',
)(payFullyForLayawayBase);
/**
 * @param { import("../types/SalesDocument").SaleDocumentResponse} sale
 */
const payPartiallyForLayawayBase = sale => async (dispatch, getState) => {
  try {
    const { before, on, after } = getPluginLifecycleHook('onPickupLayaway')(
      getState(),
    );

    await dispatch(before(sale));

    const threw = Symbol('threw');

    const modifiedSale = await dispatch(on(sale, sale)).catch(() => threw);
    if (modifiedSale === threw) {
      return;
    }

    await dispatch(resetShoppingCart());
    // TODO: Also pass products and customer explicitly
    //  products: .rows2
    //  customer: .clientID
    await dispatch(
      setCustomer({
        data: modifiedSale.clientID,
        recalculateShoppingCart: false,
      }),
    );
    await dispatch(setCurrentSalesDocument(modifiedSale));
    await dispatch(
      setCurrentSalesDocPayments({
        documentID: modifiedSale.id,
      }),
    );
    // NB! Make sure that cart is recalculated AFTER sale document and customer are set.
    // Certain document and customer fields might impact calculation and omitting them will result in
    // incorrectly calculated cart, for example, twice applied promotions.
    await dispatch(addMultiProducts(modifiedSale.rows));

    dispatch(closeModalPage());

    dispatch(openModalPage({ component: mp.layaway, isPopup: true }));

    await dispatch(after(sale, modifiedSale));
  } catch (error) {
    // In case there was some sort of error between layaway pickup process (f.e. customer, cur doc, cur doc payments were set, but the cart failed to get set, need to reset everything to prevent partial redux state)
    dispatch(revertPickup());
  }
};

export const payPartiallyForLayaway = withProgressAlert(
  'layaway:alerts.loadingPartialLayaway',
)(payPartiallyForLayawayBase);

/**
 * @param { import("../types/SalesDocument").SaleDocumentResponse} offer
 */
const pickupOfferBase = offer => async (dispatch, getState) => {
  const { before, on, after } = getPluginLifecycleHook('onPickupOffer')(
    getState(),
  );

  await dispatch(before(offer));
  const { clientID, rows, id, notes, internalNotes } = offer;

  const currentSale = getCurrentSalesDocument(getState());
  const shoppingCartEmpty = !getHasProductsInShoppingCart(getState());

  const currentSaleToArray = currentSale =>
    currentSale ? currentSale.split(',').filter(s => s !== '') : [];

  const finalSale = {
    ...currentSale,
    baseDocumentIDs: currentSaleToArray(currentSale.baseDocumentIDs)
      .concat([id])
      .join(','),
    ...(shoppingCartEmpty
      ? {
          notes,
          internalNotes,
        }
      : {}),
  };

  if (!rows.length) {
    return;
  }

  const threw = Symbol('threw');
  const modifiedOffer = await dispatch(on(offer, finalSale)).catch(() => threw);
  if (modifiedOffer === threw) {
    return;
  }

  const setOfferData = async () => {
    dispatch(setCurrentSalesDocument(modifiedOffer));
    await dispatch(setCustomer({ data: clientID }));
    // NB! Make sure that cart is recalculated AFTER sale document and customer are set.
    // Certain document and customer fields might impact calculation and omitting them will result in
    // incorrectly calculated cart, for example, twice applied promotions.
    dispatch(addMultiProducts(rows));
    dispatch(closeModalPage());
  };

  if (shoppingCartEmpty) {
    try {
      await setOfferData();
    } catch (offerSettingError) {
      dispatch(revertPickup());
      return;
    }
    await dispatch(after(offer, modifiedOffer));
    return;
  }

  try {
    await new Promise((resolve, reject) =>
      dispatch(
        createConfirmation(resolve, reject, {
          title: i18next.t('offer:confirmation.title'),
          body: i18next.t('offer:confirmation.body'),
        }),
      ),
    );
    // If OK is pressed, add the products to the shopping cart.
    try {
      await setOfferData();
    } catch (offerSettingError) {
      dispatch(revertPickup());
    }
  } catch (error) {
    // If Cancel is pressed, just return back to the offers screen
  }

  await dispatch(after(offer, modifiedOffer));
};

export const pickupOffer = withProgressAlert('offer:pickup.loading')(
  pickupOfferBase,
);

/**
 * @param { import("../types/SalesDocument").SaleDocumentResponse} invoice
 */
const pickupInvoiceBase = invoice => async (dispatch, getState) => {
  const { before, on, after } = getPluginLifecycleHook('onPickupInvoice')(
    getState(),
  );

  await dispatch(before(invoice));

  const threw = Symbol('threw');
  const propSale = await dispatch(
    on(invoice, { ...invoice, modifiedRows: [] }),
  ).catch(() => threw);

  if (propSale === threw) {
    return;
  }

  const [customers, originalPayments] = await Promise.all([
    getCustomers({
      customerID: invoice.clientID,
      getBalanceInfo: 1,
      getBalanceWithoutPrepayments: 1,
    }),
    saleApi.getPayments({ documentID: invoice.id }),
  ]);

  await dispatch(previousModalPage());

  await dispatch(
    openPaymentModal({
      props: {
        ignoreCurrent: true,
        salesDocument: propSale,
        total: propSale.total,
        customer: customers[0],
        originalPayments,
        currentSalesDocument: propSale,
      },
    }),
  );

  await dispatch(after(invoice, propSale));
};

export const pickupInvoice = withProgressAlert('unpaidInvoice:pickup.loading')(
  pickupInvoiceBase,
);

export const sendInvoiceByEmail = ({ invoiceID, enteredAddress }) => async (
  dispatch,
  getState,
) => {
  const { before, on, after } = getPluginLifecycleHook('onSendInvoiceByEmail')(
    getState(),
  );

  await dispatch(before({ invoiceID, enteredAddress }));
  try {
    dispatch(
      addWarning(i18next.t('payment:confirmView.alerts.sendingEmail'), {
        errorType: 'Receipt email',
        dismissible: false,
        selfDismiss: false,
      }),
    );
    const [fullSaleDoc] = await api.getSalesDocuments({
      invoiceID,
    });
    const defaultEmailInvoiceParams = getDefaultEmailInvoiceParams(
      fullSaleDoc.typeID,
      fullSaleDoc.type,
      fullSaleDoc.invoiceNo,
    )(getState());

    const useReceiptTemplates = getUseReceiptTemplates(getState());
    const templateID = getReceiptTemplateIdByDocType(fullSaleDoc.type)(
      getState(),
    );

    const initialEmailRequests = [
      {
        ...defaultEmailInvoiceParams,
        templateID: useReceiptTemplates ? Number(templateID) : 0,
        salesDocID: Number(invoiceID),
        recipientEmail: enteredAddress,
      },
    ];
    const { emailRequests } = await dispatch(
      on(
        { invoiceID, enteredAddress },
        { emailRequests: initialEmailRequests, fullSaleDoc },
      ),
    );
    const response = await sendSalesDocumentByEmail(emailRequests);
    if (response[0].error) {
      throw new Error(response[0].error);
    }
    dispatch(dismissType('Receipt email'));
    dispatch(
      addSuccess(
        i18next.t('payment:confirmView.alerts.sendingEmail', {
          context: 'done',
        }),
        {
          errorType: 'Receipt email',
          selfDismiss: true,
        },
      ),
    );
    await dispatch(after({ invoiceID, enteredAddress }));
  } catch (err) {
    console.warn('Failed to send invoice by email', err);
    dispatch(dismissType('Receipt email'));
    dispatch(
      addError(
        i18next.t('payment:confirmView.alerts.sendingEmail', {
          context: 'failed',
        }),
        {
          dismissible: false,
          selfDismiss: true,
          errorType: 'Receipt email',
        },
      ),
    );
  }
};

/* Order cancellation - throws to payment screen instantly */
export const cancelOrderAction = sale => async dispatch => {
  dispatch(dismissAll());

  const customerPaid =
    Number(sale.paid) +
    (sale.returnedPayments?.map(p => Number(p.sum)).reduce(add, 0) ?? 0);

  const [customer] = await getCustomers({
    customerID: sale.clientID,
    getBalanceInfo: 1,
    getBalanceWithoutPrepayments: 1,
  });
  return dispatch(
    openPaymentModal({
      props: {
        isReturn: true,
        ignoreCurrent: true,
        payButtonClicked: true,
        salesDocument: {
          type: 'ORDER',
          invoiceState: 'CANCELLED',
          id: sale.id,
          modifiedRows: [],
        },
        customer,
        total: -customerPaid,
      },
    }),
  );
};
